import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { IListOptions, IAPIListResponse, IListItem } from '@newroom-connect/library/interfaces';

import { EntityService } from '../entity/entity.service';
import { LoggingService } from '../logging/logging.service';

export interface IProjectCustomTerminologyEntry {
  id: string;
  term: string;
  translations: IProjectCustomTerminologyTranslation[];
  createdAt: string;
  updatedAt: string;
  projectId: string;
}

export interface IProjectCustomTerminologyTranslation {
  id: string;
  languageCode: string;
  translation: string;
  precedence: number;
  language: {
    code: string;
    name: string;
    precedence: number;
  };
}

@Injectable({
  providedIn: 'root'
})
export class ProjectCustomTerminologyService extends EntityService {
  private readonly loggingService = inject(LoggingService);

  public searchableProperties = ['term'];

  /**
   * List custom terminology entries for a specific project with optional filtering and pagination.
   *
   * @param projectId The ID of the project to list custom terminology from.
   * @param options Optional list options for filtering, sorting, and pagination.
   *
   * @returns Observable of custom terminology entries with pagination metadata.
   */
  public listProjectCustomTerminology(projectId: string, options?: IListOptions): Observable<IAPIListResponse<IProjectCustomTerminologyEntry>> {
    if (!projectId) {
      this.loggingService.error('Project ID is required for listing custom terminology');
      return of({ data: [], meta: { pagination: { page: 1, pageSize: 20, pageCount: 0, total: 0 } } });
    }

    this.loggingService.debug('Listing project custom terminology:', { projectId, options });

    return super.list<IProjectCustomTerminologyEntry>(`projects/${projectId}/custom-terminologies`, undefined, options);
  }

  /**
   * Get a specific custom terminology entry by ID.
   *
   * @param projectId
   * @param id The terminology ID to retrieve.
   *
   * @returns Observable of the custom terminology entry.
   * @throws Error when projectId or id is missing.
   */
  public getProjectCustomTerminologyEntry(projectId: string, id: string): Observable<IProjectCustomTerminologyEntry> {
    if (!projectId || !id) {
      this.loggingService.error('Project ID and terminology ID are required');
      throw new Error('Project ID and terminology ID are required');
    }

    this.loggingService.debug('Getting project custom terminology entry:', { projectId, id });

    return this.apiService.get<IProjectCustomTerminologyEntry>(`projects/${projectId}/custom-terminologies/${id}`);
  }

  /**
   * Create a new custom terminology entry.
   *
   * @param projectId
   * @param entry The terminology entry data to create.
   *
   * @returns Observable of the created custom terminology entry.
   * @throws Error when projectId is missing.
   */
  public createProjectCustomTerminologyEntry(
    projectId: string,
    entry: Omit<IProjectCustomTerminologyEntry, 'id' | 'createdAt' | 'updatedAt' | 'projectId'>
  ): Observable<IProjectCustomTerminologyEntry> {
    if (!projectId) {
      this.loggingService.error('Project ID is required for creating custom terminology');
      throw new Error('Project ID is required');
    }

    this.loggingService.debug('Creating project custom terminology entry:', { projectId, entry });

    return this.apiService.post<IProjectCustomTerminologyEntry>(`projects/${projectId}/custom-terminologies`, entry);
  }

  /**
   * Update an existing custom terminology entry.
   *
   * @param projectId
   * @param id The terminology ID to update.
   * @param entry The updated terminology entry data.
   *
   * @returns Observable of the updated custom terminology entry.
   * @throws Error when projectId or id is missing.
   */
  public updateProjectCustomTerminologyEntry(
    projectId: string,
    id: string,
    entry: Partial<Omit<IProjectCustomTerminologyEntry, 'id' | 'createdAt' | 'updatedAt' | 'projectId'>>
  ): Observable<IProjectCustomTerminologyEntry> {
    if (!projectId || !id) {
      this.loggingService.error('Project ID and terminology ID are required for updating');
      throw new Error('Project ID and terminology ID are required');
    }

    this.loggingService.debug('Updating project custom terminology entry:', { projectId, id, entry });

    return this.apiService.put<IProjectCustomTerminologyEntry>(`projects/${projectId}/custom-terminologies/${id}`, entry);
  }

  /**
   * Delete a custom terminology entry.
   *
   * @param projectId
   * @param id The terminology ID to delete.
   *
   * @returns Observable that completes when the entry is deleted.
   * @throws Error when projectId or id is missing.
   */
  public deleteProjectCustomTerminologyEntry(projectId: string, id: string): Observable<void> {
    if (!projectId || !id) {
      this.loggingService.error('Project ID and terminology ID are required for deletion');
      throw new Error('Project ID and terminology ID are required');
    }

    this.loggingService.debug('Deleting project custom terminology entry:', { projectId, id });

    return this.apiService.delete<void>(`projects/${projectId}/custom-terminologies/${id}`);
  }

  /**
   * Generate list options for alphabet filtering using case-insensitive startsWith pattern.
   * Follows the same pattern as MediaService for consistent filtering across the application.
   *
   * @param selectedLetter The selected alphabet letter ('A', 'B', etc.) or 'hash' for non-alphabetic characters.
   * @param baseOptions Optional base list options to extend.
   *
   * @returns List options configured for case-insensitive alphabet filtering.
   */
  public static generateAlphabetFilterOptions(selectedLetter: string, baseOptions?: IListOptions): IListOptions {
    const listOptions: IListOptions = {
      ...baseOptions,
      filters: {
        ...baseOptions?.filters,
        AND: [...(baseOptions?.filters?.['AND'] || [])]
      }
    };

    if (!listOptions.filters) {
      listOptions.filters = {};
    }

    if (!listOptions.filters['AND']) {
      listOptions.filters['AND'] = [];
    }

    if (selectedLetter !== 'all' && selectedLetter !== 'hash') {
      // Handle letters A-Z with case-insensitive matching
      const upperLetter = selectedLetter.toUpperCase();
      const lowerLetter = selectedLetter.toLowerCase();

      listOptions.filters['AND'].push({
        OR: [
          { term: { startsWith: upperLetter } },
          { term: { startsWith: lowerLetter } }
        ]
      });
    }
    // For 'hash' and 'all', we don't add any filters - just load everything

    return listOptions;
  }

  /**
   * Transform terminology entries to list items for display.
   *
   * @param entries Array of terminology entries from service.
   *
   * @returns Array of list items formatted for display.
   */
  public static transformToListItems(entries: IProjectCustomTerminologyEntry[]): IListItem[] {
    if (!entries || entries.length === 0) {
      return [];
    }

    return entries.map(entry => ({
      value: {
        id: entry.id,
        title: entry.term,
        subtitle: ProjectCustomTerminologyService.generateSubtitleFromTranslations(entry.translations)
      },
      isSelected: false
    }));
  }

  /**
   * Generate subtitle from translations array showing language codes and translation count.
   *
   * @param translations Array of translations for the terminology entry.
   *
   * @returns Formatted subtitle string.
   */
  public static generateSubtitleFromTranslations(translations: IProjectCustomTerminologyTranslation[]): string {
    if (!translations || translations.length === 0) {
      return 'No translations';
    }

    const translationStrings = translations
      .sort((a, b) => a.precedence - b.precedence)
      .map(t => `${t.languageCode.toUpperCase()}: ${t.translation}`);

    const translationCount = translations.length;

    return `${translationStrings.join(', ')} | ${translationCount} translation${translationCount !== 1 ? 's' : ''}`;
  }

  /**
   * Convert list items back to project form structure for updates.
   *
   * @param listItems Array of list items to convert.
   * @param originalEntries Original terminology entries for reference.
   *
   * @returns Array of project custom terminology update objects.
   */
  public static convertListItemsToProjectFormat(listItems: IListItem[], originalEntries: IProjectCustomTerminologyEntry[]): Array<{ term: string; translations: Array<{ languageCode: string; translation: string; precedence: number; }>; }> {
    if (!listItems || listItems.length === 0) {
      return [];
    }

    return listItems.map(item => {
      const originalEntry = originalEntries.find(entry => entry.id === item.value['id']);

      if (!originalEntry) {
        return {
          term: item.value['title'] as string,
          translations: []
        };
      }

      return {
        term: originalEntry.term,
        translations: originalEntry.translations.map(t => ({
          languageCode: t.languageCode,
          translation: t.translation,
          precedence: t.precedence
        }))
      };
    });
  }
}
